import {
  FieldCategory,
  FlowchartNode,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  FlowchartAnswer,
  FlowchartResult,
  FlowchartSection,
  FlowchartState,
  isSingleTransition,
} from 'features/libra/sections/flowchartSection/flowchartTypes'
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { processFlowchartAnswer } from './flowchartStateHelpers'

const createDefaultState = (startNode: string): FlowchartState => ({
  currentNodeId: startNode,
  answers: {},
  history: [],
  collectedFlags: [],
  isComplete: false,
  result: null,
})

interface FlowchartContextValue {
  // State
  state: FlowchartState
  currentNode: FlowchartNode | undefined
  currentNodeId: string

  // Actions
  answerQuestion: (answer: FlowchartAnswer) => void
  acknowledgeFlag: () => void
  goBack: () => void
  reset: () => void

  // Config
  section: FlowchartSection
  ontologyFields: Record<string, FlowchartNode>

  // Result
  getResult: () => FlowchartResult | null
}

const FlowchartContext = createContext<FlowchartContextValue | null>(null)

interface FlowchartProviderProps {
  section: FlowchartSection
  ontologyFields: Record<string, FlowchartNode>
  initialState?: FlowchartState
  onComplete?: (result: FlowchartResult, state: FlowchartState) => void
}

export const FlowchartProvider: FC<
  PropsWithChildren<FlowchartProviderProps>
> = ({ children, section, ontologyFields, initialState, onComplete }) => {
  const [state, setState] = useState<FlowchartState>(
    initialState || createDefaultState(section.startNode)
  )

  const currentNode = useMemo(
    () => ontologyFields[state.currentNodeId],
    [ontologyFields, state.currentNodeId]
  )

  const computeNextState = useCallback(
    (nextNodeId: string, baseState: FlowchartState): FlowchartState => {
      const nextNode = ontologyFields[nextNodeId]
      if (!nextNode) {
        console.error(`Unknown node: ${nextNodeId}`)
        return baseState
      }

      // Use switch on category for type narrowing
      switch (nextNode.category) {
        case FieldCategory.FLAG:
          return {
            ...baseState,
            currentNodeId: nextNodeId,
            collectedFlags: [...baseState.collectedFlags, nextNodeId],
          }
        case FieldCategory.OUTCOME:
          return {
            ...baseState,
            currentNodeId: nextNodeId,
            isComplete: true,
            result: nextNode.result,
          }
        case FieldCategory.QUESTION:
          return {
            ...baseState,
            currentNodeId: nextNodeId,
          }
      }
    },
    [ontologyFields]
  )

  const answerQuestion = useCallback(
    (answer: FlowchartAnswer) => {
      const transitions = section.nodes[state.currentNodeId]
      if (!transitions) {
        console.error(`No transitions for node: ${state.currentNodeId}`)
        return
      }

      const result = processFlowchartAnswer(state, answer, transitions)
      if (!result) {
        console.error(`Could not determine next node from answer: ${answer}`)
        return
      }

      const newState = computeNextState(result.nextNodeId, result.updatedState)
      setState(newState)

      // Call onComplete in the event handler (safe - outside render)
      if (newState.isComplete && newState.result !== null) {
        onComplete?.(
          {
            result: newState.result,
            flags: newState.collectedFlags,
            answers: newState.answers,
          },
          newState
        )
      }
    },
    [section.nodes, state, computeNextState, onComplete]
  )

  const acknowledgeFlag = useCallback(() => {
    const transitions = section.nodes[state.currentNodeId]
    if (!transitions || !isSingleTransition(transitions)) {
      console.error(
        `Flag node should have 'next' transition: ${state.currentNodeId}`
      )
      return
    }

    // Calculate new state outside of setState
    const baseState: FlowchartState = {
      ...state,
      history: [...state.history, state.currentNodeId],
    }
    const newState = computeNextState(transitions.next, baseState)

    // Update state
    setState(newState)

    // Call onComplete in the event handler (safe - outside render)
    if (newState.isComplete && newState.result !== null) {
      onComplete?.(
        {
          result: newState.result,
          flags: newState.collectedFlags,
          answers: newState.answers,
        },
        newState
      )
    }
  }, [section.nodes, state, computeNextState, onComplete])

  const goBack = useCallback(() => {
    setState((prev) => {
      if (prev.history.length === 0) {
        return prev
      }

      const newHistory = [...prev.history]
      const previousNodeId = newHistory.pop()!

      // Remove answer for the node we're going back from. `Object.fromEntries`
      // + `Object.entries().filter(...)` gives the same result as a
      // destructure-and-discard without needing an unused-var eslint suppression.
      const remainingAnswers = Object.fromEntries(
        Object.entries(prev.answers).filter(([k]) => k !== prev.currentNodeId)
      )

      // Remove flag if current node was a flag
      const currentNode = ontologyFields[prev.currentNodeId]
      const newFlags =
        currentNode?.category === FieldCategory.FLAG
          ? prev.collectedFlags.filter((f) => f !== prev.currentNodeId)
          : prev.collectedFlags

      return {
        ...prev,
        currentNodeId: previousNodeId,
        history: newHistory,
        answers: remainingAnswers,
        collectedFlags: newFlags,
        isComplete: false,
        result: null,
      }
    })
  }, [ontologyFields])

  const reset = useCallback(() => {
    setState(createDefaultState(section.startNode))
  }, [section.startNode])

  const getResult = useCallback((): FlowchartResult | null => {
    if (!state.isComplete || state.result === null) {
      return null
    }
    return {
      result: state.result,
      flags: state.collectedFlags,
      answers: state.answers,
    }
  }, [state])

  const value = useMemo<FlowchartContextValue>(
    () => ({
      state,
      currentNode,
      currentNodeId: state.currentNodeId,
      answerQuestion,
      acknowledgeFlag,
      goBack,
      reset,
      section,
      ontologyFields,
      getResult,
    }),
    [
      state,
      currentNode,
      answerQuestion,
      acknowledgeFlag,
      goBack,
      reset,
      section,
      ontologyFields,
      getResult,
    ]
  )

  return (
    <FlowchartContext.Provider value={value}>
      {children}
    </FlowchartContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

export function useFlowchart(): FlowchartContextValue {
  const context = useContext(FlowchartContext)
  if (!context) {
    throw new Error('useFlowchart must be used within FlowchartProvider')
  }
  return context
}
